import { CommonModule, Location } from "@angular/common";
import { Component, inject, signal } from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { PaginatorState } from "primeng/paginator";
import {
  BehaviorSubject,
  Subject,
  distinctUntilChanged,
  finalize,
  startWith,
  switchMap,
  tap,
} from "rxjs";
import { AppLayoutComponent } from "../../../../shared/components/app-layout/app-layout.component";
import { EventsManagementService } from "../../../services/events-management.service";
import { SelectedCityService } from "../../../services/selected-city.service";
import { EventsListFilters } from "../../../services/types/events-management-request.type";
import { EventsFilterComponent } from "../../../shared/components/events-filter/events-filter.component";
import { EventsListComponent } from "./events-list/events-list.component";

type ListableFilters = Omit<EventsListFilters, "page" | "per_page">;

const DEFAULT_PAGE_SIZE = 20;

@Component({
  selector: "app-events-list-container",
  imports: [
    CommonModule,
    EventsListComponent,
    EventsFilterComponent,
    AppLayoutComponent,
  ],
  templateUrl: "./events-list-container.component.html",
  styleUrl: "./events-list-container.component.scss",
})
export class EventsListContainerComponent {
  private readonly eventsService = inject(EventsManagementService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly selectedCityService = inject(SelectedCityService);

  // Seed filters from URL synchronously so the list loads once on init.
  initialFilters: ListableFilters = this.readFiltersFromUrlSnapshot();
  private readonly filters$ = new BehaviorSubject<ListableFilters>(
    this.initialFilters,
  );
  private readonly pageChange$ = new Subject<PaginatorState>();
  private readonly hasCityFromUrl =
    !!this.route.snapshot.queryParamMap.get("city");
  private userTouchedCityFilter = false;

  isLoading = signal(false);
  cities$ = this.eventsService.getActiveCities();
  categories$ = this.eventsService.getEventsCategories();

  private readonly _bindCityFromService = this.bindCityFromService();

  // A filter change restarts pagination at page 0 (via startWith) inside the
  // same stream, so it triggers a single request instead of two (filters +
  // page reset). switchMap cancels the superseded request, and isLoading is
  // released in finalize so it also covers the error case.
  eventsList$ = this.filters$.pipe(
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    switchMap((filters) =>
      this.pageChange$.pipe(
        startWith<PaginatorState>({ page: 0, rows: DEFAULT_PAGE_SIZE }),
        tap(() => this.isLoading.set(true)),
        switchMap((page) =>
          this.eventsService
            .getAllEvents({
              page: page.page,
              per_page: page.rows,
              ...filters,
            })
            .pipe(finalize(() => this.isLoading.set(false))),
        ),
      ),
    ),
  );

  handlePageChange(page: PaginatorState) {
    this.pageChange$.next(page);
  }

  handleFiltersChange(filters: ListableFilters) {
    // Evita disparar requisição duplicada quando o filtro emitido é igual ao
    // que já está aplicado (ex: emissão inicial do componente de filtro).
    if (JSON.stringify(filters) === JSON.stringify(this.filters$.value)) {
      return;
    }

    if ((filters.city ?? null) !== (this.filters$.value.city ?? null)) {
      this.userTouchedCityFilter = true;
    }

    // Page reset is handled by the startWith inside eventsList$, so a single
    // request is fired.
    this.filters$.next(filters);
    this.writeFiltersToUrl(filters);
  }

  private bindCityFromService(): void {
    if (this.hasCityFromUrl) {
      return;
    }
    toObservable(this.selectedCityService.selectedCity)
      .pipe(distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((city) => {
        if (this.userTouchedCityFilter) return;
        const current = this.filters$.value;
        if ((current.city ?? null) === (city ?? null)) return;

        const next: ListableFilters = {
          ...current,
          city: city ?? undefined,
        };
        this.filters$.next(next);
        this.initialFilters = { ...next };
      });
  }

  private readFiltersFromUrlSnapshot(): ListableFilters {
    const params = this.route.snapshot.queryParamMap;
    const categoryIdRaw = params.get("category");
    const cityFromUrl = params.get("city");
    const fallbackCity = this.selectedCityService.selectedCity();
    return {
      query: params.get("query") ?? undefined,
      city: cityFromUrl ?? fallbackCity ?? undefined,
      start_date: params.get("start_date") ?? undefined,
      end_date: params.get("end_date") ?? undefined,
      category: categoryIdRaw ? Number(categoryIdRaw) : undefined,
    };
  }

  private writeFiltersToUrl(filters: ListableFilters): void {
    // Update the URL via Location (not Router) so that scroll position
    // restoration doesn't kick in and jump the page back to the top.
    const urlTree = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: {
        query: filters.query || null,
        city: filters.city || null,
        start_date: filters.start_date || null,
        end_date: filters.end_date || null,
        category: filters.category ?? null,
      },
      queryParamsHandling: "merge",
    });

    this.location.replaceState(this.router.serializeUrl(urlTree));
  }
}
